import {
  Box,
  Button,
  Container,
  Grid,
  GridItem,
  Select,
} from '@sainsburys-tech/fable'
import dayjs from 'dayjs'
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useBlocker, useNavigate } from 'react-router-dom'

import { isFeatureEnabled } from '../../../common/api/featureApi'
import { useApiClient } from '../../../common/AppContext/appContext'
import InfoBanner from '../../../common/components/Banners/InfoBanner'
import ErrorRefresh from '../../../common/components/ErrorRefresh'
import Header from '../../../common/components/Header'
import Loading from '../../../common/components/Loading'
import SaveModal from '../../../common/components/Modals/SaveModal'
import ProgressLoading from '../../../common/components/ProgressLoading'
import { Context } from '../../../common/Context/context'
import { ErrorMessage } from '../../../common/enums/ErrorMessage'
import { Feature } from '../../../common/enums/FeatureEnum'
import { headerPlannerTitles } from '../../../common/enums/HeaderItems'
import { Menu } from '../../../common/enums/MenuEnum'
import { PlannerName } from '../../../common/enums/PlannerNameEnum'
import useComponentInitialization from '../../../common/hooks/useComponentInit'
import useErrorHandler from '../../../common/hooks/useErrorHandler'
import { getUserStore } from '../../../utils/localStore'
import { getControlGroups } from '../../api/controlGroupApi'
import { getPlan, updatePlanItems } from '../../api/planApi'
import { IControlGroup } from '../../types/IControlGroup'
import { IPlan } from '../../types/IPlan'
import { IPlanControlGroup } from '../../types/IPlanControlGroup'
import { IPlanItem } from '../../types/IPlanItem'
import { IPlanItemUpdate } from '../../types/IPlanItemUpdate'
import { IPlannerGroup } from '../../types/IPlannerGroup'
import SalesForecastTable from './SalesForecastTable'

const ForecastView = () => {
  const { dispatch } = useContext(Context)
  const navigate = useNavigate()
  const apiClient = useApiClient()

  const store = useMemo(() => getUserStore(), [])
  const [isDataLoading, setIsDataLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [allowScroll, setAllowScroll] = useState(false)
  const [controlGroups, setControlGroups] = useState<IControlGroup[]>([])
  const [selectedControlGroupId, setSelectedControlGroupId] = useState(-1)
  const [planDateOptions] = useState<Date[]>(
    Array.from({ length: 13 }, (_, i) => {
      const rangeDate = new Date()
      rangeDate.setDate(rangeDate.getDate() + (i - 6))
      return rangeDate
    }),
  )
  const [planDate, setPlanDate] = useState<Date>(new Date())
  const [plan, setPlan] = useState<IPlan>({
    planId: 0,
    planDate: new Date(),
    planControlGroups: [],
    eventName: '',
  })

  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [reload, setReload] = useState(false)

  const [selectedPlanDateIndexN0, setSelectedPlanDateIndexN0] = useState(6)
  const [selectedPlanDateIndexN1, setSelectedPlanDateIndexN1] = useState(6)
  const [targetParamsN1, setTargetParamsN1] = useState('')
  const [targetPathnameN1, setTargetPathnameN1] = useState('')
  const [unsavedChanges, setUnsavedChanges] = useState(false)
  const blocker = useBlocker(unsavedChanges)
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false)
  const [isNewAlgorithm, setIsNewAlgorithm] = useState(false)

  const { handleError } = useErrorHandler(setErrorMessage)

  const doBlock = useCallback(
    (unsaved: boolean) => {
      setUnsavedChanges(unsaved)
    },
    [],
  )

  const blockedLocation =
    blocker.state === 'blocked' ? blocker.location : undefined

  useEffect(() => {
    if (blockedLocation) {
      setTargetParamsN1(blockedLocation.search)
      setTargetPathnameN1(blockedLocation.pathname)
      setIsSaveModalOpen(true)
    }
  }, [blockedLocation])

  const checkAndDoNavigation = (): boolean => {
    if (targetPathnameN1 !== '') {
      setUnsavedChanges(false)
      if (blocker.state === 'blocked') {
        blocker.proceed()
      } else {
        navigate(`${targetPathnameN1}${targetParamsN1}`)
      }
      return true
    }
    return false
  }

  const { headerNavigationItems, isLoading: initLoading } =
    useComponentInitialization(
      apiClient,
      store,
      dispatch,
      PlannerName.Bakery,
      handleError,
    )

  const isLoading = initLoading || isDataLoading

  useEffect(() => {
    if (selectedControlGroupId === -1 && controlGroups.length > 0) {
      setSelectedControlGroupId(controlGroups[0].controlGroupId)
    }
  }, [controlGroups, selectedControlGroupId])

  useEffect(() => {
    setIsDataLoading(true)

    const loadPlanData = async () => {
      try {
        const [controlGroups, plan, isNewAlgorithm] = await Promise.all([
          getControlGroups(apiClient),
          getPlan(apiClient, dayjs(planDate).format('YYYY-MM-DD')),
          isFeatureEnabled(apiClient, Feature.NewAlgorithm, store.storeId),
        ])
        setControlGroups(controlGroups)
        setPlan(plan)
        setIsNewAlgorithm(isNewAlgorithm)
      } catch (error) {
        handleError(ErrorMessage.ForecastView.FailedToLoad, error as Error)
      } finally {
        setIsDataLoading(false)
        if (reload) {
          setReload(false)
        }
      }
    }

    loadPlanData()

    doBlock(false)
  }, [apiClient, planDate, reload, handleError, doBlock])

  useEffect(() => {
    // componentDidUpdate functionality
    if (allowScroll === true) {
      const el = document.querySelector('.c-bakery-plan-item-input')
      setAllowScroll(false)
      if (el !== null) {
        el.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest',
        })
      }
    }
  }, [allowScroll])

  const isPastForecast = (): boolean => {
    if (plan.planId === 0) {
      return false
    }

    return dayjs(plan.planDate).diff(dayjs(), 'day') < 0
  }

  const isEditButtonDisabled = (): boolean => {
    if (saving === true) {
      return true
    }

    if (editing === true) {
      return false
    }

    return (
      isLoading === true ||
      plan.planControlGroups === null ||
      !hasEditableSkus()
    )
  }

  const hasEditableSkus = (): boolean =>
    plan.planControlGroups!.filter((planControlGroup) => {
      if (planControlGroup.controlGroupId !== selectedControlGroupId) {
        return false
      }

      return (
        planControlGroup.plannerGroups.filter(
          (plannerGroup) =>
            plannerGroup.planItems.filter(
              (planItem) => planItem.isSkuEditable === true,
            ).length > 0,
        ).length > 0
      )
    }).length > 0

  const doPlanDateChange = (dateIndex: number) => {
    setPlanDate(planDateOptions[dateIndex])
    setEditing(false)
    setSelectedPlanDateIndexN0(dateIndex)
  }

  const handleEdit = () => {
    if (editing === true) {
      setSaving(true)
      const planItemUpdateList: IPlanItemUpdate[] = []
      plan.planControlGroups!.forEach((planControlGroup) => {
        planControlGroup.plannerGroups.forEach((plannerGroup) => {
          plannerGroup.planItems
            .filter((planItem) => planItem.modified === true)
            .forEach((planItem) => {
              planItemUpdateList.push({
                planItemId: planItem.planItemId,
                produceQuantity: planItem.produceQuantity,
                skuId: planItem.skuId,
                totalSalesForecast: planItem.totalSalesForecast,
              })
            })
        })
      })

      if (planItemUpdateList.length === 0) {
        setEditing(false)
        setSaving(false)
        setSelectedPlanDateIndexN0(selectedPlanDateIndexN1)
        doBlock(false)
        return
      }

      updatePlanItems(apiClient, plan.planId, planItemUpdateList)
        .then((result) => {
          const nextPlanDateIndex = selectedPlanDateIndexN1
          setEditing(false)
          setSaving(false)
          setSelectedPlanDateIndexN0(nextPlanDateIndex)
          doBlock(false)

          if (result.isSuccess === false) {
            handleError(result.message)
          }
          if (checkAndDoNavigation() === false) {
            doPlanDateChange(nextPlanDateIndex)
          }

          // fetch the plan data again from the server once being updated
          // so that the updatedAt and updatedBy will be fetched from server.
          setReload(true)
        })
        .catch((e: Error) => {
          handleError(ErrorMessage.ForecastView.FailedToUpdate, e as Error)
          setSaving(false)
        })
    } else {
      setEditing(!editing)
      setAllowScroll(true)
    }
  }

  const handleProductionTargetChange = (
    planItemId: number,
    updatedQuantity: number,
  ) => {
    doBlock(true)

    const targetControlGroup = plan.planControlGroups?.find(
      (pcg: IPlanControlGroup) => pcg.controlGroupId === selectedControlGroupId,
    )
    const updatedPlannerGroups = targetControlGroup?.plannerGroups.reduce(
      (accPgs: IPlannerGroup[], curPg: IPlannerGroup) => {
        if (curPg.planItems.find((p) => p.planItemId === planItemId)) {
          const updatedPlanItems = curPg.planItems.reduce(
            (accPis: IPlanItem[], curPi: IPlanItem) => {
              if (curPi.planItemId === planItemId) {
                return [
                  ...accPis,
                  {
                    ...curPi,
                    totalSalesForecast: updatedQuantity,
                    modified: true,
                  },
                ]
              }
              return [...accPis, curPi]
            },
            [],
          )
          return [...accPgs, { ...curPg, planItems: updatedPlanItems }]
        }
        return [...accPgs, curPg]
      },
      [],
    )

    const updatedControlGroups = plan.planControlGroups?.reduce(
      (xs: IPlanControlGroup[], y: IPlanControlGroup) => {
        if (
          targetControlGroup !== undefined &&
          y.controlGroupId === targetControlGroup.controlGroupId
        ) {
          return [
            ...xs,
            { ...y, plannerGroups: updatedPlannerGroups as IPlannerGroup[] },
          ]
        }
        return [...xs, y]
      },
      [],
    )

    setPlan({
      ...plan,
      planControlGroups: updatedControlGroups as IPlanControlGroup[],
    })
  }

  const handleSaveAndContinue = () => {
    setIsSaveModalOpen(false)
    handleEdit()
  }

  const handleNoSaveAndContinue = () => {
    const nextPlanDateIndex = selectedPlanDateIndexN1
    setEditing(false)
    setIsSaveModalOpen(false)
    setSelectedPlanDateIndexN0(nextPlanDateIndex)
    doBlock(false)

    if (checkAndDoNavigation() === false) {
      doPlanDateChange(nextPlanDateIndex)
    }
  }

  const handleCancel = () => {
    setIsSaveModalOpen(false)
    if (blocker.state === 'blocked') {
      blocker.reset()
    }
    setSelectedPlanDateIndexN1(selectedPlanDateIndexN0)
    setTargetParamsN1('')
    setTargetPathnameN1('')
  }

  return (
    <>
      {isLoading && (
        <ProgressLoading testid='loading-indicator' message='Loading...' />
      )}

      {!isLoading && (
        <Box padding='none' backgroundColor='primary'>
          <Header
            title={headerPlannerTitles.bakery}
            navItems={headerNavigationItems}
            activeMenuType={Menu.ForecastView}
          />

          {(isLoading || saving) && <Loading />}

          {!isLoading && errorMessage && (
            <ErrorRefresh message={errorMessage} />
          )}

          {!isLoading && !errorMessage && (
            <>
              <Container className='c-common-main-view-content'>
                <Grid cols={{ xs: 1, sm: 2, md: 12 }} gap='lg' center={true}>
                  <GridItem colSpan={{ md: 4 }}>
                    <Select
                      testid='date-selector'
                      id='date-selector'
                      label='Plan Date'
                      hasHiddenLabel
                      options={planDateOptions.map((d, i) => {
                        let label = dayjs(d).format('ddd Do MMMM')
                        if (i === 6) {
                          label += ' (Today)'
                        }
                        return { label, value: String(i) }
                      })}
                      value={String(selectedPlanDateIndexN0)}
                      onChange={(value) => {
                        setSelectedPlanDateIndexN1(Number(value))
                        if (!unsavedChanges) {
                          doPlanDateChange(Number(value))
                        }
                      }}
                      style={{ width: '100%' }}
                    />
                  </GridItem>
                  <GridItem colSpan={{ md: 4 }}>
                    <Select
                      testid='control-group-selector'
                      id='control-group-selector'
                      label='Control Group'
                      hasHiddenLabel
                      options={controlGroups.map((controlGroup) => ({
                        label: controlGroup.controlGroupName,
                        value: String(controlGroup.controlGroupId),
                      }))}
                      value={String(selectedControlGroupId)}
                      onChange={(value) =>
                        setSelectedControlGroupId(Number(value))
                      }
                      style={{ width: '100%' }}
                    />
                  </GridItem>
                  <GridItem
                    colSpan={{ md: 4 }}
                    style={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                    }}
                  >
                    {!isNewAlgorithm && !isPastForecast() && (
                      <Button
                        disabled={isEditButtonDisabled()}
                        onClick={() => handleEdit()}
                        variant='secondary'
                        testid={`forecast-button-${editing ? 'save' : 'edit'}`}
                        style={{ minWidth: 140 }}
                      >
                        {editing === true ? 'Save Forecast' : 'Edit Forecast'}
                      </Button>
                    )}
                  </GridItem>
                </Grid>

                <Container margin='md' className='o-common-loading-overlay'>
                  {isPastForecast() && (
                    <InfoBanner message='This forecast is in the past' />
                  )}
                  <Box padding={{ md: 'md' }} backgroundColor='secondary'>
                    <SalesForecastTable
                      testid='sales-forecast-table'
                      plan={plan}
                      controlGroupId={selectedControlGroupId}
                      editing={editing}
                      onProductionTargetChange={handleProductionTargetChange}
                    />
                  </Box>
                </Container>
              </Container>
              {isSaveModalOpen && (
                <SaveModal
                  testid='save-modal'
                  isOpen={isSaveModalOpen}
                  modalBody={
                    "You've made changes to the production plan. Would you like to save these changes?"
                  }
                  modalTitle={'Save changes?'}
                  onClose={() => handleCancel()}
                  onNo={handleNoSaveAndContinue}
                  onYes={handleSaveAndContinue}
                />
              )}
            </>
          )}
        </Box>
      )}
    </>
  )
}

export default ForecastView
